export const STORED_ASSETS_KEY = "storedAssets";
export const USER_EMBEDDING_META_KEY = "neuron:userEmbeddingMeta";

export const getStoredEmbeddings = () => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const stored = window.localStorage.getItem(STORED_ASSETS_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export const getStoredEmbeddingMeta = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(USER_EMBEDDING_META_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
};

export const handleAssetClick = (embeddingValue, navigate) => {
  // Ensure embeddingValue is a valid, non-empty array
  if (!Array.isArray(embeddingValue) || embeddingValue.length === 0) {
    console.error("Invalid or empty embedding value:", embeddingValue);
    return;
  }

  console.log("Embedding Value before storing:", embeddingValue);

  // Retrieve stored embeddings from localStorage
  const storedAssets = getStoredEmbeddings();

  // Validate the embedding value and store it if valid
  if (embeddingValue.every((val) => typeof val === "number" && !isNaN(val))) {
    storedAssets.push(embeddingValue);
    localStorage.setItem(STORED_ASSETS_KEY, JSON.stringify(storedAssets));
    localStorage.setItem(
      USER_EMBEDDING_META_KEY,
      JSON.stringify({
        updatedAt: new Date().toISOString(),
        count: storedAssets.length,
        dimension: embeddingValue.length,
      })
    );
  } else {
    console.error("Embedding value contains invalid data:", embeddingValue);
    return;
  }

  // Calculate the average embedding vector across all stored vectors
  const averageEmbeddingValue = calculateAverageEmbedding(storedAssets);

  console.log("Average Embedding Vector:", averageEmbeddingValue);

  // Emit a custom event with the average embedding vector
  const event = new CustomEvent("embeddingUpdated", {
    detail: {
      message: `Clicks: ${storedAssets.length}`,
    },
  });
  window.dispatchEvent(event);

  // Navigate to the previous page or specified route
  navigate(-1);
};

export const calculateMatchPercentage = (embedding1, embedding2) => {
  if (!Array.isArray(embedding1) || !Array.isArray(embedding2)) {
    console.error("Invalid embeddings:", embedding1, embedding2);
    return 0;
  }

  if (embedding1.length !== embedding2.length) {
    console.error(
      "Embeddings are of different lengths:",
      embedding1,
      embedding2
    );
    return 0;
  }

  let dotProduct = 0;
  let magnitude1 = 0;
  let magnitude2 = 0;

  for (let i = 0; i < embedding1.length; i++) {
    const val1 = embedding1[i];
    const val2 = embedding2[i];

    if (isNaN(val1) || isNaN(val2)) {
      console.error("Found NaN or undefined in embeddings:", val1, val2);
      return 0;
    }

    dotProduct += val1 * val2;
    magnitude1 += val1 * val1;
    magnitude2 += val2 * val2;
  }

  magnitude1 = Math.sqrt(magnitude1);
  magnitude2 = Math.sqrt(magnitude2);

  if (magnitude1 === 0 || magnitude2 === 0) {
    console.error(
      "Magnitude of one of the embeddings is zero:",
      magnitude1,
      magnitude2
    );
    return 0;
  }

  const cosineSimilarity = dotProduct / (magnitude1 * magnitude2);
  const matchPercentage = cosineSimilarity * 100;

  return parseFloat(matchPercentage); // No formatting
};

// Function to calculate the average embedding vector
export const calculateAverageEmbedding = (embeddings) => {
  if (embeddings.length === 0) return [];

  const numEmbeddings = embeddings.length;
  const embeddingLength = embeddings[0].length;

  // Initialize an array to hold the sum of each dimension
  const sumEmbeddings = new Array(embeddingLength).fill(0);

  // Sum each dimension across all embeddings
  embeddings.forEach((embedding) => {
    for (let i = 0; i < embeddingLength; i++) {
      sumEmbeddings[i] += embedding[i];
    }
  });

  // Calculate the average embedding vector by dividing the sum by the number of embeddings
  const averageEmbedding = sumEmbeddings.map((sum) => sum / numEmbeddings);

  return averageEmbedding;
};
